"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { NotePopulated, Quiz, QuizInfo, QuizPopulated, QuizStreamingChunk, Share } from "@vita-learning/types";
import { getQuizzesKey, getQuizzesUrl, QUIZ_URL_SEGMENT, type QuizQueryParams } from "~/shared/services/data/quizzes/query";
import { createQuiz, fetchQuizzes, getQuiz, shareQuiz, updateQuiz, generateQuiz, deleteQuiz, createManyQuizzes, createQuizShareUrl } from "~/shared/services/data/quizzes/requests";

/**
 * Hook for fetching quizzes. If `id` is provided in the params, will fetch a single quiz. Otherwise, will fetch all quizzes (optionally filtered by `folderId`).
 *
 * @param {QuizQueryParams} params the parameters for fetching quizzes
 */
export const useQuizzes = t0 => {
  const $ = _c(38);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const params = t1;
  const queryClient = useQueryClient();
  let t2;
  if ($[2] !== params) {
    t2 = getQuizzesKey(params);
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== params) {
    t3 = () => {
      if (params.disabled) {
        return [];
      }
      if (params.id) {
        return getQuiz(params.id);
      }
      const url = getQuizzesUrl(params);
      return fetchQuizzes(url);
    };
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const queryResults = useSuspenseQuery(t4);
  let t5;
  if ($[9] !== queryClient) {
    t5 = () => queryClient.invalidateQueries({
      queryKey: [QUIZ_URL_SEGMENT]
    });
    $[9] = queryClient;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const revalidateAll = t5;
  let t6;
  if ($[11] !== queryResults.data) {
    t6 = Array.isArray(queryResults.data) ? queryResults.data : [];
    $[11] = queryResults.data;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const data = t6;
  const entity = !Array.isArray(queryResults.data) ? queryResults.data : null;
  let t7;
  if ($[13] !== revalidateAll) {
    t7 = {
      mutationFn: createQuiz,
      onSuccess: revalidateAll
    };
    $[13] = revalidateAll;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const create = useMutation(t7);
  let t8;
  if ($[15] !== revalidateAll) {
    t8 = {
      mutationFn: createManyQuizzes,
      onSuccess: revalidateAll
    };
    $[15] = revalidateAll;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const createMany = useMutation(t8);
  let t9;
  if ($[17] !== revalidateAll) {
    t9 = {
      mutationFn: updateQuiz,
      onSuccess: revalidateAll
    };
    $[17] = revalidateAll;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const update = useMutation(t9);
  let t10;
  if ($[19] !== revalidateAll) {
    t10 = {
      mutationFn: deleteQuiz,
      onSuccess: revalidateAll
    };
    $[19] = revalidateAll;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const remove = useMutation(t10);
  let t11;
  if ($[21] !== revalidateAll) {
    t11 = {
      mutationFn: _temp,
      onSuccess: revalidateAll
    };
    $[21] = revalidateAll;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const share_0 = useMutation(t11);
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      mutationFn: createQuizShareUrl
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const createShareUrl = useMutation(t12);
  let t13;
  if ($[24] !== revalidateAll) {
    t13 = {
      mutationFn: _temp2,
      onSuccess: revalidateAll
    };
    $[24] = revalidateAll;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const generate = useMutation(t13);
  let t14;
  if ($[26] !== create || $[27] !== createMany || $[28] !== createShareUrl || $[29] !== data || $[30] !== entity || $[31] !== generate || $[32] !== queryResults || $[33] !== remove || $[34] !== revalidateAll || $[35] !== share_0 || $[36] !== update) {
    t14 = {
      ...queryResults,
      data,
      entity,
      create,
      createMany,
      update,
      remove,
      share: share_0,
      generate,
      createShareUrl,
      revalidateAll
    };
    $[26] = create;
    $[27] = createMany;
    $[28] = createShareUrl;
    $[29] = data;
    $[30] = entity;
    $[31] = generate;
    $[32] = queryResults;
    $[33] = remove;
    $[34] = revalidateAll;
    $[35] = share_0;
    $[36] = update;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
};
function _temp(t0) {
  const {
    id,
    share
  } = t0;
  return shareQuiz(id, share);
}
function _temp2(t0) {
  const {
    quiz,
    source,
    onStream
  } = t0;
  return generateQuiz(quiz, source, onStream);
}